import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { STATE_COLOR_MS } from './companionTheme';
import { CoreCourier } from './CoreCourier';
import type { Courier } from './courier/courier';
import type { CourierBody } from './courier/types';
import {
  DEFAULT_SETTINGS,
  ObserverCtx,
  ObserverRegistryCtx,
  type AnchorMeta,
  type ObserverApi,
  type ObserverEvent,
  type ObserverGesture,
  type ObserverPlacement,
  type ObserverRegistry,
  type ObserverSettings,
  type ObserverShape,
  type ObserverState,
} from './observerContext';
import { ORBIT_PLAN } from './observerEvents';
import { useCoreEngine, type Anchor } from './useCoreEngine';

/*
  Движок «Наблюдателя» (docs/observer-design.md).

  Почему так, а не через состояние React:
  - позиция, размер и деформация пишутся ПРЯМО в style через ссылки. Иначе каждый кадр прокрутки
    перерисовывал бы оболочку — болезнь нынешнего индикатора навигации (setState на каждом кадре
    ResizeObserver);
  - перелёт — один CSS-переход по преобразованию и размерам, а не покадровая петля;
  - слежение за курсором, прокруткой и размерами сведено в один кадровый тик, плюс страховка
    таймером: в скрытой вкладке кадров нет вообще;
  - деформация считается из длины перелёта и живёт только в движении (в покое корпус строгий).

  Два дома (проп home): orbit — площадка /design/observer, всё ниже про позицию и орбиту — о нём;
  core — оболочка, движок в useCoreEngine: своего тела нет, тело — ядро знака (spec-3), орбиты, поиска
  блока под курсором и слежения за курсором нет. К событию ядро несёт курьер (CoreCourier, spec-3-motion);
  его тело сменное: проп body, по умолчанию — капля и LiquidBody у цели (courier/defaultBody.ts).
*/

/** Настройки площадки; у оболочки — свой ключ (storageKey), чтобы площадка не выключала помощника там. */
const STORAGE_KEY = 'kaname.observer.settings';

/** Размер свободной формы и отступ от края экрана. */
const FREE_SIZE: Record<ObserverShape, number> = { dock: 40, dot: 26, planet: 48 };
const FREE_MARGIN = 28;
/** Отступ, когда садится рядом с чужим элементом. */
const BESIDE_GAP = 8;
/** Насколько он отступает внутрь от угла блока (примерно сантиметр на обычном экране). */
const CORNER_INSET = 34;
/** Сколько курсор должен постоять над блоком, прежде чем он перелетит: лечит суету. */
const INTENT_MS = 380;
/** Минимальная пауза между перелётами. */
const CALM_BETWEEN_MS = 700;

const PULL = { quiet: 4, companion: 22 };
const LOOK = { quiet: 6, companion: 20 };
/** Предел деформации при движении. */
const STRETCH = { restrained: 0.06, pronounced: 0.14, cartoon: 0.28 };

const GESTURE_MS: Record<ObserverGesture, number> = {
  bounce: 720,
  shake: 520,
  shiver: 620,
  flash: 520,
  nod: 480,
  pulse: 900,
};

/** Орбита знает один элемент на адрес — последний заявивший о себе. */
function lastAnchor(map: Map<string, Anchor[]>, id: string | null): Anchor | undefined {
  const list = id ? map.get(id) : undefined;
  return list?.[list.length - 1];
}

function readSettings(storageKey: string): ObserverSettings {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_SETTINGS;
    const saved = JSON.parse(raw) as Partial<ObserverSettings>;
    return {
      ...DEFAULT_SETTINGS,
      ...saved,
      reactions: { ...DEFAULT_SETTINGS.reactions, ...(saved.reactions ?? {}) },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

interface ObserverProviderProps {
  children: ReactNode;
  /** Дом: орбита площадки (по умолчанию — площадка не меняется) или ядро знака в оболочке. */
  home?: 'orbit' | 'core';
  /** Открыт ящик уведомлений, поиск, ИИ или модалка: курьер не летит, визит прерывается (этап D2). */
  suspended?: boolean;
  /** Где хранить настройки в браузере. */
  storageKey?: string;
  /** Тело курьера (дом core); без него — тело по умолчанию: капля в пути, LiquidBody у цели. */
  body?: CourierBody;
}

export function ObserverProvider({
  children,
  home = 'orbit',
  suspended = false,
  storageKey = STORAGE_KEY,
  body,
}: ObserverProviderProps) {
  const core = home === 'core';
  const [settings, setSettingsState] = useState<ObserverSettings>(() => readSettings(storageKey));
  const [state, setState] = useState<ObserverState>('idle');
  const [shape, setShape] = useState<ObserverShape>('dock');
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [placement, setPlacementState] = useState<ObserverPlacement>('free');
  const [onActivate, setOnActivate] = useState<(() => void) | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const skinRef = useRef<HTMLDivElement>(null);

  /** Реестр: у адреса может быть несколько элементов (строка на главной и шапка вкладки). */
  const anchors = useRef(new Map<string, Anchor[]>());
  const homeId = useRef<string | null>(null);
  const targetId = useRef<string | null>(null);
  const placementRef = useRef<ObserverPlacement>('free');
  const cursor = useRef({ x: 0, y: 0 });
  const lastBox = useRef({ x: 0, y: 0, w: FREE_SIZE.dot, h: FREE_SIZE.dot });
  const needsUpdate = useRef(true);
  const teleporting = useRef(false);
  const flyingTimer = useRef(0);
  const gestureTimer = useRef(0);
  const settingsRef = useRef(settings);
  const shapeRef = useRef(shape);
  const lastFlight = useRef(0);
  const busy = useRef(false);
  const blockId = useRef<string | null>(null);
  const blockCheckedAt = useRef(0);
  const pendingBlock = useRef<string | null>(null);
  const pendingSince = useRef(0);
  const lastMove = useRef(0);

  useEffect(() => {
    settingsRef.current = settings;
    shapeRef.current = shape;
    needsUpdate.current = true;
  }, [settings, shape]);

  // ---------- позиция ----------

  const targetBox = useCallback(() => {
    const a = lastAnchor(anchors.current, targetId.current);
    const size = FREE_SIZE[shapeRef.current];

    if (a) {
      const r = a.el.getBoundingClientRect();
      const onScreen =
        r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
      if (placementRef.current === 'cover') {
        return { x: r.left, y: r.top, w: r.width, h: r.height, visible: onScreen };
      }

      // На углу блока: не на самой кромке, а отступив внутрь — так он не режется рамкой
      // и не выглядит приклеенным. Если место посадки уехало за экран, он гаснет, а не
      // прилипает к верхней кромке поверх чужих элементов.
      if (placementRef.current === 'corner') {
        // Центр сидит ровно на верхней границе блока (наполовину снаружи), отступ справа сохраняется.
        const x = r.right - CORNER_INSET - size / 2;
        const y = r.top - size / 2;
        const inView = y > 4 && y < window.innerHeight - size - 4 && onScreen;
        return { x, y, w: size, h: size, visible: inView };
      }

      // У края: ВСЕГДА снаружи элемента. Внутрь не садимся — на кнопке он перекрывал бы надпись.
      const fitsRight = r.right + BESIDE_GAP + size < window.innerWidth;
      const x = fitsRight ? r.right + BESIDE_GAP : Math.max(BESIDE_GAP, r.left - BESIDE_GAP - size);
      const y = Math.min(
        Math.max(BESIDE_GAP, r.top + r.height / 2 - size / 2),
        window.innerHeight - size - BESIDE_GAP,
      );
      return { x, y, w: size, h: size, visible: onScreen };
    }

    const baseX = window.innerWidth - FREE_MARGIN - size;
    const baseY = window.innerHeight - FREE_MARGIN - size;
    const pull = settingsRef.current.followCursor ? PULL[settingsRef.current.presence] : 0;
    const dx = Math.max(-pull, Math.min(pull, (cursor.current.x - baseX) / 40));
    const dy = Math.max(-pull, Math.min(pull, (cursor.current.y - baseY) / 40));
    return { x: baseX + dx, y: baseY + dy, w: size, h: size, visible: true };
  }, []);

  /** Деформация по длине перелёта: вытянуться вдоль движения, затем сплющиться на посадке. */
  const deform = useCallback((dx: number, dy: number) => {
    const tilt = tiltRef.current;
    if (!tilt) return;
    const s = settingsRef.current;
    if (s.calm) return;
    const dist = Math.hypot(dx, dy);
    if (dist < 12) return;
    const limit = STRETCH[s.motion];
    const k = Math.min(1, dist / 700) * limit;
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    tilt.style.setProperty('--fly-angle', `${angle.toFixed(1)}deg`);
    tilt.style.setProperty('--fly-stretch', (1 + k).toFixed(3));
    tilt.style.setProperty('--fly-squeeze', (1 - k * 0.55).toFixed(3));
    tilt.dataset.phase = 'fly';
    window.clearTimeout(flyingTimer.current);
    flyingTimer.current = window.setTimeout(() => {
      tilt.dataset.phase = 'land';
      tilt.style.setProperty('--fly-stretch', (1 - k * 0.45).toFixed(3));
      tilt.style.setProperty('--fly-squeeze', (1 + k * 0.5).toFixed(3));
      window.clearTimeout(flyingTimer.current);
      flyingTimer.current = window.setTimeout(() => {
        tilt.dataset.phase = 'rest';
        tilt.style.setProperty('--fly-stretch', '1');
        tilt.style.setProperty('--fly-squeeze', '1');
      }, 200);
    }, 300);
  }, []);

  const apply = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const t = targetBox();
    const prev = lastBox.current;
    const dx = t.x - prev.x;
    const dy = t.y - prev.y;
    const far = Math.abs(dx) > window.innerWidth * 0.55 || Math.abs(dy) > window.innerHeight * 0.55;

    if (far && !teleporting.current) {
      teleporting.current = true;
      root.style.opacity = '0';
      window.setTimeout(() => {
        root.style.transition = 'none';
        root.style.transform = `translate3d(${t.x}px, ${t.y}px, 0)`;
        root.style.width = `${t.w}px`;
        root.style.height = `${t.h}px`;
        void root.offsetWidth;
        root.style.transition = '';
        root.style.opacity = t.visible ? '1' : '0';
        teleporting.current = false;
      }, 130);
    } else if (!teleporting.current) {
      deform(dx, dy);
      root.style.transform = `translate3d(${t.x}px, ${t.y}px, 0)`;
      root.style.width = `${t.w}px`;
      root.style.height = `${t.h}px`;
      root.style.opacity = t.visible ? '1' : '0';
    }
    lastBox.current = { x: t.x, y: t.y, w: t.w, h: t.h };

    const skin = skinRef.current;
    if (skin) {
      const s = settingsRef.current;
      if (s.followCursor && !s.calm) {
        const cx = t.x + t.w / 2;
        const cy = t.y + t.h / 2;
        const lx = Math.max(-1, Math.min(1, (cursor.current.x - cx) / 400));
        const ly = Math.max(-1, Math.min(1, (cursor.current.y - cy) / 400));
        const look = LOOK[s.presence];
        skin.style.setProperty('--look-x', `${(lx * look).toFixed(1)}%`);
        skin.style.setProperty('--look-y', `${(ly * look).toFixed(1)}%`);
      } else {
        skin.style.setProperty('--look-x', '0%');
        skin.style.setProperty('--look-y', '0%');
      }
    }
  }, [targetBox, deform]);

  useEffect(() => {
    // Дом core: орбиты, поиска блока под курсором и слежения за курсором нет.
    if (core) return;
    let raf = 0;
    const tick = () => {
      if (needsUpdate.current) {
        needsUpdate.current = false;
        apply();
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    const mark = () => {
      needsUpdate.current = true;
    };
    const onPointer = (e: PointerEvent) => {
      cursor.current = { x: e.clientX, y: e.clientY };
      needsUpdate.current = true;
      // Какой блок под курсором: компаньон садится на его угол. Проверяем не чаще, чем раз
      // в 120 мс — перебор десятка прямоугольников на каждое движение мыши не нужен.
      const now = Date.now();
      if (now - blockCheckedAt.current < 120) return;
      blockCheckedAt.current = now;
      const s = settingsRef.current;
      if (!s.enabled || s.presence !== 'companion' || !s.followCursor || busy.current) return;
      // Цель — только блок содержимого. По пунктам меню он больше не скачет: там своё
      // постоянное выделение, а прыжки по узким строкам выглядели суетливо.
      let found: string | null = null;
      for (const [id, list] of anchors.current) {
        const a = list[list.length - 1];
        if (!a || a.meta.role !== 'block') continue;
        const r = a.el.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
          found = id;
          break;
        }
      }

      // Намерение: перелетаем, только если курсор задержался над новым блоком, и не чаще,
      // чем раз в CALM_BETWEEN_MS. Пролетел мимо — он даже не шевельнётся.
      if (found !== pendingBlock.current) {
        pendingBlock.current = found;
        pendingSince.current = now;
        return;
      }
      if (now - pendingSince.current < INTENT_MS) return;
      if (now - lastMove.current < CALM_BETWEEN_MS) return;

      if (found !== blockId.current) {
        blockId.current = found;
        lastMove.current = now;
        targetId.current = found;
        placementRef.current = found ? 'corner' : 'free';
        setPlacementState(found ? 'corner' : 'free');
        setAnchorId(found);
        needsUpdate.current = true;
      }
    };
    window.addEventListener('scroll', mark, { passive: true, capture: true });
    window.addEventListener('resize', mark);
    window.addEventListener('pointermove', onPointer, { passive: true });
    // Страховка: в скрытой вкладке кадров нет, но позиция должна оставаться верной.
    const sync = window.setInterval(() => {
      needsUpdate.current = true;
      apply();
    }, 500);
    const first = window.setTimeout(apply, 0);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearInterval(sync);
      window.clearTimeout(first);
      window.removeEventListener('scroll', mark, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', mark);
      window.removeEventListener('pointermove', onPointer);
    };
  }, [apply, core]);

  // ---------- цвет: темп смены несёт срочность ----------

  useEffect(() => {
    const skin = skinRef.current;
    if (skin) skin.style.setProperty('--color-ms', `${STATE_COLOR_MS[state]}ms`);
  }, [state]);

  // ---------- мир ----------

  /** Смена формы меняет и посадку: подложка накрывает пункт, точка и планета садятся у края. */
  const setShapeAndPlacement = useCallback((next: ObserverShape) => {
    setShape(next);
    shapeRef.current = next;
    const hasAnchor = !!targetId.current && anchors.current.has(targetId.current);
    const nextPlacement: ObserverPlacement = !hasAnchor ? 'free' : next === 'dock' ? 'cover' : 'edge';
    placementRef.current = nextPlacement;
    setPlacementState(nextPlacement);
    needsUpdate.current = true;
  }, []);

  const registerAnchor = useCallback((id: string, el: HTMLElement, meta: AnchorMeta) => {
    const list = anchors.current.get(id) ?? [];
    const own = list.find((a) => a.el === el);
    if (own) own.meta = meta;
    else anchors.current.set(id, [...list, { el, meta }]);
    needsUpdate.current = true;
  }, []);

  const unregisterAnchor = useCallback((id: string, el?: HTMLElement) => {
    const rest = el ? (anchors.current.get(id) ?? []).filter((a) => a.el !== el) : [];
    if (rest.length > 0) anchors.current.set(id, rest);
    else anchors.current.delete(id);
    if (targetId.current === id && rest.length === 0) {
      targetId.current = homeId.current;
      placementRef.current = homeId.current ? 'cover' : 'free';
    }
    needsUpdate.current = true;
  }, []);

  const goHome = useCallback(() => {
    // Меню он больше не занимает: выделение активного пункта постоянное и своё (утилита
    // nav-active). Наблюдатель живёт на углу блока, с которым работают, а тихий характер
    // просто стоит на орбите у края экрана и реагирует только на события.
    const companion = settingsRef.current.presence === 'companion';
    const block = companion ? blockId.current : null;
    targetId.current = block;
    placementRef.current = block ? 'corner' : 'free';
    setPlacementState(block ? 'corner' : 'free');
    setAnchorId(block);
    setShape(shapeRef.current === 'dock' ? 'dot' : shapeRef.current);
    needsUpdate.current = true;
  }, []);

  const setHome = useCallback(
    (id: string | null) => {
      homeId.current = id;
      goHome();
    },
    [goHome],
  );

  const highlight = useCallback((id: string | null, on: boolean) => {
    const a = lastAnchor(anchors.current, id);
    if (!a) return;
    a.el.classList.toggle('observer-hint', on);
  }, []);

  const react = useCallback((gesture: ObserverGesture) => {
    const skin = skinRef.current;
    if (!skin || settingsRef.current.calm) return;
    skin.dataset.gesture = gesture;
    window.clearTimeout(gestureTimer.current);
    gestureTimer.current = window.setTimeout(() => {
      delete skin.dataset.gesture;
    }, GESTURE_MS[gesture]);
  }, []);

  const pointAt = useCallback(
    (id: string, gesture: ObserverGesture = 'bounce') => {
      const a = lastAnchor(anchors.current, id);
      if (!a || settingsRef.current.calm) return;
      busy.current = true;
      lastFlight.current = Date.now();
      targetId.current = id;
      placementRef.current = 'edge';
      setPlacementState('edge');
      setAnchorId(id);
      setShape('dot');
      needsUpdate.current = true;
      highlight(id, true);
      window.setTimeout(() => react(gesture), 340);
      window.setTimeout(() => {
        highlight(id, false);
        busy.current = false;
        goHome();
      }, 1900);
    },
    [goHome, highlight, react],
  );

  const notify = useCallback(
    (event: ObserverEvent) => {
      const s = settingsRef.current;
      if (!s.enabled || !s.reactions[event.kind]) return;
      const { gesture, state: nextState } = ORBIT_PLAN[event.kind];
      setState(nextState);
      if (nextState !== 'idle') window.setTimeout(() => setState('idle'), 2600);
      // Летим только к заметным целям: колокольчик, строка данных. К кнопке или полю
      // он не подлетает — садиться вплотную к кнопке выглядит навязчиво, достаточно жеста.
      const anchorId = Array.isArray(event.anchorId) ? event.anchorId[0] : event.anchorId;
      const target = lastAnchor(anchors.current, anchorId ?? null);
      const worthFlying = !!target && (target.meta.role === 'source' || target.meta.role === 'item');
      const canFly =
        s.guidance && !s.calm && !busy.current && worthFlying && Date.now() - lastFlight.current > 6000;
      if (canFly && anchorId) pointAt(anchorId, gesture);
      else react(gesture);
    },
    [pointAt, react],
  );

  // ---------- дом core: движок помощника в оболочке ----------

  const courierRef = useRef<Courier | null>(null);
  const engine = useCoreEngine({
    active: core,
    settings,
    settingsRef,
    anchorsRef: anchors,
    suspended,
    courierRef,
  });
  /** Элемент адреса: видимый из заявивших, иначе последний. */
  const anchor = useCallback((id: string) => {
    const list = anchors.current.get(id) ?? [];
    const shown = list.find((a) => a.el.isConnected && a.el.getClientRects().length > 0);
    return (shown ?? list[list.length - 1])?.el ?? null;
  }, []);
  const { cueHome } = engine;
  const coreReact = useCallback((gesture: ObserverGesture) => cueHome(gesture), [cueHome]);
  /** Дом core: «показать» без курьера — жест дома (с курьером — этап D2). */
  const corePoint = useCallback(
    (_id: string, gesture: ObserverGesture = 'bounce') => cueHome(gesture),
    [cueHome],
  );

  // ---------- настройки ----------

  const setSettings = useCallback(
    (patch: Partial<ObserverSettings>) => {
      const next: ObserverSettings = {
        ...settingsRef.current,
        ...patch,
        reactions: { ...settingsRef.current.reactions, ...(patch.reactions ?? {}) },
      };
      // Ссылку обновляем сразу: движок и goHome читают именно её, а состояние React
      // применится только к следующему кадру.
      settingsRef.current = next;
      setSettingsState(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // приватный режим: настройки просто не переживут перезагрузку
      }
      // Смена характера меняет само место покоя: тихий садится домой, компаньон уходит на орбиту.
      if (patch.presence !== undefined) goHome();
    },
    [goHome, storageKey],
  );

  const { politeRef, assertiveRef } = engine;
  const registry = useMemo<ObserverRegistry>(
    () => ({ registerAnchor, unregisterAnchor }),
    [registerAnchor, unregisterAnchor],
  );

  const api = useMemo<ObserverApi>(
    () => ({
      settings,
      state: core ? engine.state : state,
      shape,
      anchorId,
      placement,
      onActivate,
      home,
      away: engine.away,
      holds: engine.holds,
      awaiting: engine.awaiting,
      setSettings,
      setState: core ? engine.setState : setState,
      setShape: setShapeAndPlacement,
      setOnActivate,
      hold: engine.hold,
      resolve: engine.resolve,
      setCorePort: engine.setCorePort,
      anchor,
      registerAnchor,
      unregisterAnchor,
      goHome,
      setHome,
      pointAt: core ? corePoint : pointAt,
      react: core ? coreReact : react,
      notify: core ? engine.notify : notify,
      rootRef,
      tiltRef,
      skinRef,
    }),
    [
      settings,
      core,
      engine,
      state,
      shape,
      anchorId,
      placement,
      onActivate,
      home,
      setSettings,
      setShapeAndPlacement,
      anchor,
      registerAnchor,
      unregisterAnchor,
      goHome,
      setHome,
      coreReact,
      corePoint,
      pointAt,
      react,
      notify,
    ],
  );

  return (
    <ObserverRegistryCtx.Provider value={registry}>
      <ObserverCtx.Provider value={api}>
        {children}
        {/* Строка для читалки (дом core): событие слышно сразу и без ядра; фокус не двигается никогда. */}
        {core && (
          <>
            <div ref={politeRef} aria-live="polite" className="sr-only" />
            <div ref={assertiveRef} aria-live="assertive" className="sr-only" />
            <CoreCourier courierRef={courierRef} body={body} state={engine.state} />
          </>
        )}
      </ObserverCtx.Provider>
    </ObserverRegistryCtx.Provider>
  );
}
